import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: attach JWT
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// --- Types ---

export interface AdminUser {
  email: string;
  name: string;
  picture?: string;
}

export interface TenantFunnel {
  signedUp: boolean;
  importCompleted: boolean;
  domainSharing: boolean;
  connectionAdded: boolean;
  twoUsers: boolean;
  syncCompleted: boolean;
}

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  ownerEmail?: string;
  userCount: number;
  employeeCount: number;
  createdAt?: string;
  termsAcceptedAt?: string;
  termsVersion?: string;
  lastAdminLoginAt?: string;
  lastUserLoginAt?: string;
  activeUsers7d?: number;
  activeUsers30d?: number;
  lastActionAt?: string;
  funnel?: TenantFunnel;
  accessMode?: 'domain' | 'invite_only' | null;
  allowedDomains?: string[];
}

export interface PlatformUser {
  email: string;
  name?: string;
  tenants?: string[];
  lastLoginAt?: string;
  createdAt?: string;
}

export interface PlatformMetrics {
  totalTenants: number;
  activeTenants: number;
  suspendedTenants: number;
  totalPlatformUsers: number;
  wauTenants?: number;
  mauTenants?: number;
  wauUsers?: number;
  mauUsers?: number;
}

export interface CohortRow {
  weekStart: string;
  size: number;
  weeks: { week: number; active: number }[];
}

export interface TenantNote {
  id: string;
  body: string;
  author: string;
  createdAt: string;
}

// --- Auth ---

export async function loginWithGoogle(credential: string): Promise<{ token: string; user: AdminUser }> {
  const response = await api.post('/auth/login', { credential });
  return response.data;
}

export async function fetchCurrentUser(): Promise<AdminUser> {
  const response = await api.get('/auth/me');
  return response.data;
}

// --- Tenants ---

export async function fetchTenants(includeDeleted = false): Promise<TenantSummary[]> {
  const response = await api.get('/tenants', { params: { include_deleted: includeDeleted || undefined } });
  return response.data;
}

export async function fetchTenant(id: string): Promise<any> {
  const response = await api.get(`/tenants/${id}`);
  return response.data;
}

export async function updateTenantStatus(id: string, status: string, reason?: string): Promise<any> {
  const response = await api.put(`/tenants/${id}/status`, { status, reason });
  return response.data;
}

export async function deleteTenant(id: string): Promise<any> {
  const response = await api.delete(`/tenants/${id}`);
  return response.data;
}

// --- Platform Admins ---

export interface PlatformAdmin {
  email: string;
  name?: string;
  isPlatformAdmin: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export async function fetchAdmins(): Promise<PlatformAdmin[]> {
  const response = await api.get('/admins');
  return response.data;
}

export async function inviteAdmin(email: string): Promise<PlatformAdmin> {
  const response = await api.post('/admins', { email });
  return response.data;
}

export async function removeAdmin(email: string): Promise<PlatformAdmin> {
  const response = await api.delete(`/admins/${encodeURIComponent(email)}`);
  return response.data;
}

// --- AI Events ---

export interface AIEvent {
  id: string;
  timestamp: string;
  operation: string;
  error: string;
  model: string;
  tenantId?: string;
  level: string;
}

export async function fetchAIEvents(limit: number = 50): Promise<AIEvent[]> {
  const response = await api.get(`/ai-events?limit=${limit}`);
  return response.data;
}

// --- Tenant detail ---

export interface TenantUser {
  email: string;
  name?: string;
  role?: string;
  lastLoginAt?: string;
  invitedBy?: string;
  edits30d?: number;
}

export interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  ownerEmail?: string;
  createdAt?: string;
  users: TenantUser[];
  connectionCount: number;
  sandboxCount: number;
  usage?: { users?: number; employees?: number };
  allowedDomains?: string[];
  termsAcceptedBy?: string;
  termsAcceptedAt?: string;
  termsVersion?: string;
  activity?: { activeUsers7d: number; activeUsers30d: number; lastActionAt?: string };
  funnel?: TenantFunnel;
  accessMode?: 'domain' | 'invite_only' | null;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  action: string;
  category: string;
  actorEmail?: string;
  targetName?: string;
  targetEmail?: string;
  targetDepartment?: string;
  description: string;
  details?: Record<string, any>;
  batchId?: string;
  hasChanges?: boolean;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  nextCursor: string | null;
}

export interface AuditLogParams {
  limit?: number;
  cursor?: string;
  category?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  hasChanges?: boolean;
}

export async function fetchTenantDetail(id: string): Promise<TenantDetail> {
  const response = await api.get(`/tenants/${id}`);
  return response.data;
}

export async function fetchTenantAuditLogs(tenantId: string, params: AuditLogParams = {}): Promise<AuditLogResponse> {
  const response = await api.get(`/tenants/${tenantId}/audit-logs`, { params });
  return response.data;
}

export async function fetchTenantAuditBatch(tenantId: string, batchId: string, limit = 50, cursor?: string): Promise<AuditLogResponse> {
  const response = await api.get(`/tenants/${tenantId}/audit-logs/batch/${batchId}`, { params: { limit, cursor } });
  return response.data;
}

// --- Feedback ---

export interface FeedbackItem {
  id: string;
  type: 'feature_request' | 'bug_report';
  title: string;
  description: string;
  page: string;
  userEmail: string;
  userName: string;
  tenantId: string;
  tenantSlug: string;
  status: 'open' | 'acknowledged' | 'done' | 'closed';
  createdAt: string;
}

export async function fetchFeedback(status?: string): Promise<FeedbackItem[]> {
  const params = status ? { status } : {};
  const response = await api.get('/feedback', { params });
  return response.data;
}

export async function updateFeedbackStatus(id: string, status: string): Promise<FeedbackItem> {
  const response = await api.put(`/feedback/${id}/status`, { status });
  return response.data;
}

// --- Metrics ---

export async function fetchMetrics(): Promise<PlatformMetrics> {
  const response = await api.get('/metrics');
  return response.data;
}

export async function fetchCohorts(weeks = 8): Promise<CohortRow[]> {
  const response = await api.get('/cohorts', { params: { weeks } });
  return response.data;
}

// --- Tenant Notes ---

export async function fetchTenantNotes(tenantId: string): Promise<TenantNote[]> {
  const response = await api.get(`/tenants/${tenantId}/notes`);
  return response.data;
}

export async function createTenantNote(tenantId: string, body: string): Promise<TenantNote> {
  const response = await api.post(`/tenants/${tenantId}/notes`, { body });
  return response.data;
}

export async function deleteTenantNote(tenantId: string, noteId: string): Promise<void> {
  await api.delete(`/tenants/${tenantId}/notes/${noteId}`);
}

// --- Public API keys ---

export interface ApiKeyPublic {
  id: string;
  tenant_id: string;
  name: string;
  key_prefix: string;
  key_last4: string;
  status: 'active' | 'revoked';
  created_at: string;
  created_by?: string | null;
  revoked_at?: string | null;
  last_used_at?: string | null;
}

export interface ApiKeyCreated extends ApiKeyPublic {
  token: string;
}

export async function fetchApiKeys(tenantId: string): Promise<ApiKeyPublic[]> {
  const response = await api.get(`/tenants/${tenantId}/api-keys`);
  return response.data;
}

export async function createApiKey(tenantId: string, name: string): Promise<ApiKeyCreated> {
  const response = await api.post(`/tenants/${tenantId}/api-keys`, { name });
  return response.data;
}

export async function revokeApiKey(tenantId: string, keyId: string): Promise<ApiKeyPublic> {
  const response = await api.post(`/tenants/${tenantId}/api-keys/${keyId}/revoke`);
  return response.data;
}
